import React from 'react';
import { Eye, Zap, X, ArrowLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

/**
 * Secret Menu Modal - Hidden debug menu for mobile devices
 * Triggered by pressing and holding all 4 corners of the puzzle board for 1 second
 */
export function SecretMenuModal({ 
  isOpen, 
  onClose, 
  onShowHint, 
  onAutoComplete,
  isComplete = false
}) {
  const handleShowHint = () => {
    onShowHint?.();
    onClose();
  };

  const handleAutoComplete = () => {
    onAutoComplete?.();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={cn(
          "sm:max-w-[320px] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-auto p-0 overflow-y-auto max-h-[90vh] sm:max-h-[85vh] box-border",
          "bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900",
          "border border-zinc-700/50"
        )}
        data-testid="secret-menu-modal"
      >
        <DialogHeader className="p-4 pb-2 border-b border-zinc-700/50">
          <DialogTitle className="flex items-center gap-2 text-zinc-100">
            {/* Back button for mobile */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="h-8 w-8 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700/50"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span className="text-lg">🔓</span>
            Secret Menu
          </DialogTitle>
        </DialogHeader>
        
        <div className="p-4 space-y-3">
          {/* Option 1: Show Hint Overlay */}
          <Button
            variant="outline"
            className={cn(
              "w-full h-auto py-4 flex items-center gap-3",
              "bg-zinc-800/50 border-zinc-700 hover:bg-zinc-700/50",
              "hover:border-amber-500/50 transition-all duration-200",
              "text-zinc-100"
            )}
            onClick={handleShowHint}
            data-testid="secret-menu-hint-btn"
          >
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Eye className="h-5 w-5 text-amber-400" />
            </div>
            <div className="flex-1 text-left">
              <div className="font-medium">Show Hint</div>
              <div className="text-xs text-zinc-400">
                Reveals the solution overlay
              </div>
            </div>
          </Button>

          {/* Option 2: Auto-Complete */}
          <Button
            variant="outline"
            className={cn(
              "w-full h-auto py-4 flex items-center gap-3",
              "bg-zinc-800/50 border-zinc-700 hover:bg-zinc-700/50",
              "hover:border-emerald-500/50 transition-all duration-200",
              "text-zinc-100",
              isComplete && "opacity-50 cursor-not-allowed"
            )}
            onClick={handleAutoComplete}
            disabled={isComplete}
            data-testid="secret-menu-autocomplete-btn"
          >
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Zap className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="flex-1 text-left">
              <div className="font-medium">Auto-Complete</div>
              <div className="text-xs text-zinc-400">
                {isComplete ? "Puzzle already complete" : "Instantly solve the puzzle"}
              </div>
            </div>
          </Button>
        </div>

        {/* Footer hint */}
        <div className="px-4 pb-4">
          <p className="text-[10px] text-zinc-500 text-center">
            Tip: Hold all 4 corners for 1 second to open this menu
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
